import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmt, fmtDate } from "@/lib/format";
import { Upload } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";

const AY_ADLARI = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const ayLabel = (m) => { const [y, mo] = (m || "").split("-"); return mo ? `${AY_ADLARI[parseInt(mo, 10) - 1]} ${y}` : m; };

export default function GencerKasa() {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [detay, setDetay] = useState(null);
  const [busy, setBusy] = useState(false);
  const [aylar, setAylar] = useState([]);
  const [ay, setAy] = useState("");
  const [period, setPeriod] = useState("month"); // month | 3 | 6 | 12 | all
  const [view, setView] = useState("kasa"); // kasa | masraf
  const fileRef = useRef(null);

  const loadAylar = () => api.get("/gencer/aylar").then(({ data }) => {
    const list = data.aylar || [];
    setAylar(list);
    setAy((prev) => (prev && list.includes(prev)) ? prev : (list[0] || ""));
  }).catch(() => {});
  useEffect(() => { loadAylar(); }, []);

  const load = () => {
    const iso = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
    let params = null;
    if (period === "all") {
      params = {};
    } else if (period === "month") {
      if (!ay) { setData({ gunler: [], toplam: {} }); return Promise.resolve(); }
      params = { ay };
    } else {
      const anchor = ay || aylar[0];
      if (!anchor) { setData({ gunler: [], toplam: {} }); return Promise.resolve(); }
      const [y, m] = anchor.split("-").map(Number);
      const n = parseInt(period, 10);
      params = { start: iso(new Date(y, m - 1 - (n - 1), 1)), end: iso(new Date(y, m, 0)) };
    }
    return api.get("/gencer/aylik", { params }).then(({ data }) => setData(data)).catch(() => toast.error("Gencer verisi yüklenemedi"));
  };
  useEffect(() => { load(); }, [ay, period]);

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm("Gencer Kasa Excel'i içe aktarılacak. Yalnızca dosyadaki aya/günlere ait veriler güncellenecek; diğer aylar korunacak. Devam?")) { e.target.value = ""; return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/gencer/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.gun} gün, ${data.satis} satış aktarıldı`);
      await loadAylar();
      load();
    } catch (err) { toast.error("İçe aktarma başarısız"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const openGun = async (tarih) => {
    setSel(tarih);
    try { const { data } = await api.get("/gencer/gunluk", { params: { tarih } }); setDetay(data); }
    catch (e) { toast.error("Günlük yüklenemedi"); }
  };

  const geriAl = async (tarih, kapsam) => {
    const mesaj = kapsam === "tam"
      ? `${fmtDate(tarih)} gününü TAM GERİ AL:\n\n• Gençer Ana Kasa'dan bu gün silinecek\n• Bu güne ait cari föy kasa ödemeleri (vade düşüşleri) de GERİ ALINACAK\n\nDevam edilsin mi?`
      : `${fmtDate(tarih)} gününü SADECE ANA KASA'dan geri al:\n\n• Gençer Ana Kasa'dan bu gün silinecek\n• Cari föydeki vade ödemelerine DOKUNULMAYACAK\n\nDevam edilsin mi?`;
    if (!window.confirm(mesaj)) return;
    try {
      const { data } = await api.post("/gencer/gun-geri-al", { tarih, kapsam });
      toast.success(`Geri alındı: Gençer ${data.gencer_gun_silinen} gün${kapsam === "tam" ? `, ${data.cari_etkilenen_kayit} cari kayıt` : ""}`);
      setSel(null); setDetay(null);
      await loadAylar(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Geri alma başarısız"); }
  };

  const g = data?.gunler || [];
  const t = data?.toplam || {};
  const chart = g.filter((d) => (d.kar || 0) !== 0 || (d.kasa || 0) !== 0).map((d) => ({ gun: (d.tarih || "").slice(8, 10), KAR: d.kar, KASA: d.kasa }));

  const aylikOzet = (() => {
    const map = {};
    g.forEach((d) => {
      const ym = (d.tarih || "").slice(0, 7);
      if (!ym) return;
      const m = map[ym] || (map[ym] = { ym, pesinat: 0, gelen_senet: 0, phkk: 0, shkk: 0, af: 0, cikis: 0, masraf: 0 });
      ["pesinat", "gelen_senet", "phkk", "shkk", "af", "cikis", "masraf"].forEach((k) => { m[k] += d[k] || 0; });
    });
    return Object.values(map).map((m) => ({ ...m, giris: m.pesinat + m.gelen_senet + m.phkk + m.shkk, gider: m.af + m.cikis + m.masraf })).sort((a, b) => a.ym.localeCompare(b.ym));
  })();

  return (
    <div data-testid="gencer-kasa">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-[#8C8377]">Gençer Ltd. — Günlük Satış ve Kasa (aylık özet otomatik)</div>
        <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="gencer-import-btn" className="flex items-center gap-1.5 px-3 py-2 border border-[#E7DECC] bg-white rounded-sm text-sm font-medium hover:bg-[#EFE7D6] disabled:opacity-50"><Upload className="w-4 h-4 text-[#B8933F]" /> {busy ? "Aktarılıyor…" : "Excel'den İçe Aktar"}</button>
        <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={doImport} data-testid="gencer-import-input" />
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4 p-4 bg-white border border-[#E7DECC] rounded-sm">
        <div>
          <label className="kicker block mb-1">DÖNEM</label>
          <div className="flex gap-1" data-testid="gencer-donem">
            {[["month", "Aylık"], ["3", "3 Aylık"], ["6", "6 Aylık"], ["12", "Yıllık"], ["all", "Tümü"]].map(([k, lbl]) => (
              <button key={k} onClick={() => { setPeriod(k); setSel(null); setDetay(null); }} data-testid={`gencer-donem-${k}`} className={`px-3 py-2 rounded-sm text-sm font-medium transition-colors duration-150 ${period === k ? "bg-[#B8933F] text-white" : "bg-white border border-[#E7DECC] text-[#8C8377] hover:bg-[#EFE7D6]"}`}>{lbl}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="kicker block mb-1">{period === "month" ? "AY SEÇİN" : period === "all" ? "AY (tümü)" : "BİTİŞ AYI"}</label>
          <select value={ay} onChange={(e) => { setAy(e.target.value); setSel(null); setDetay(null); }} disabled={period === "all"} data-testid="gencer-ay-select" className="px-3 py-2 border border-[#E7DECC] rounded-sm text-sm bg-white min-w-[180px] disabled:opacity-50">
            {aylar.length === 0 && <option value="">Veri yok</option>}
            {aylar.map((m) => <option key={m} value={m}>{ayLabel(m)}</option>)}
          </select>
        </div>
        <div className="ml-auto text-sm text-[#8C8377]">{g.length} gün · {aylar.length} ay yüklü</div>
      </div>

      <div className="flex gap-1 mb-4" data-testid="gencer-view-toggle">
        {[["kasa", "Kasa Tablosu"], ["masraf", "Masraf Kalemleri"]].map(([k, lbl]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`gencer-view-${k}`} className={`px-4 py-2 rounded-sm text-sm font-medium transition-colors duration-150 ${view === k ? "bg-[#26231E] text-white" : "bg-white border border-[#E7DECC] text-[#8C8377] hover:bg-[#EFE7D6]"}`}>{lbl}</button>
        ))}
      </div>

      {view === "kasa" && g.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="gencer-gelir-gider">
          <div className="bg-white border border-[#E7DECC] rounded-sm p-4" style={{ borderTop: "3px solid #2F6E4E" }} data-testid="gencer-gelirler">
            <div className="kicker mb-2 text-[#2F6E4E]">GELİRLER</div>
            {[["Peşinat", t.pesinat], ["Gelen Senet", t.gelen_senet], ["P.H.K.K", t.phkk], ["S.H.K.K", t.shkk]].map(([l, v]) => (
              <div key={l} className="flex justify-between text-sm py-0.5"><span className="text-[#8C8377]">{l}</span><span className="tabular-nums">{fmt(v || 0)}</span></div>
            ))}
            <div className="flex justify-between text-sm py-1.5 mt-1 border-t border-[#E7DECC] font-bold"><span>Toplam Gelir</span><span className="tabular-nums text-[#2F6E4E]" data-testid="gencer-toplam-gelir">{fmt((t.pesinat || 0) + (t.gelen_senet || 0) + (t.phkk || 0) + (t.shkk || 0))}</span></div>
          </div>
          <div className="bg-white border border-[#E7DECC] rounded-sm p-4" style={{ borderTop: "3px solid #A63A3A" }} data-testid="gencer-giderler">
            <div className="kicker mb-2 text-[#A63A3A]">GİDERLER</div>
            {[["AF (Alış)", t.af], ["Çıkış", t.cikis], ["Masraf", t.masraf]].map(([l, v]) => (
              <div key={l} className="flex justify-between text-sm py-0.5"><span className="text-[#8C8377]">{l}</span><span className="tabular-nums">{fmt(v || 0)}</span></div>
            ))}
            <div className="flex justify-between text-sm py-1.5 mt-1 border-t border-[#E7DECC] font-bold"><span>Toplam Gider</span><span className="tabular-nums text-[#A63A3A]" data-testid="gencer-toplam-gider">{fmt((t.af || 0) + (t.cikis || 0) + (t.masraf || 0) + (t.gider_duzeltme || 0))}</span></div>
          </div>
          <div className="bg-white border border-[#E7DECC] rounded-sm p-4" style={{ borderTop: "3px solid #B8933F" }} data-testid="gencer-kar-kasa">
            <div className="kicker mb-2 text-[#B8933F]">KÂR & KASA</div>
            {[["KAR", t.kar], ["Net Kar", t.net_kar]].map(([l, v]) => (
              <div key={l} className="flex justify-between text-sm py-0.5"><span className="text-[#8C8377]">{l}</span><span className="tabular-nums">{fmt(v || 0)}</span></div>
            ))}
            <div className="flex justify-between text-sm py-1.5 mt-1 border-t border-[#E7DECC] font-bold"><span>KASA (Net Bakiye)</span><span className="tabular-nums text-[#B8933F]">{fmt(t.kasa || 0)}</span></div>
          </div>
        </div>
      )}

      {view === "kasa" && period === "month" && chart.length > 0 && (
        <div className="bg-white border border-[#E7DECC] rounded-sm p-4 mb-6" data-testid="gencer-chart">
          <div className="kicker mb-3">GÜN GÜN KAR / KASA</div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EFE7D6" />
              <XAxis dataKey="gun" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v / 1000) + "k"} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Legend />
              <Bar dataKey="KAR" fill="#2F6E4E" radius={[3, 3, 0, 0]} />
              <Bar dataKey="KASA" fill="#B8933F" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {view === "kasa" && period === "month" && (
      <div className="bg-white border border-[#E7DECC] rounded-sm overflow-hidden mb-6">
        <div className="max-h-[460px] overflow-auto">
          <table className="w-full text-sm" data-testid="gencer-aylik-table">
            <thead className="sticky top-0 bg-[#F3ECDD] border-b border-[#E7DECC] z-10"><tr>
              {["Tarih", "AF", "SF", "Peşinat", "Gelen Senet", "P.H.K.K", "S.H.K.K", "KAR", "Net Kar", "Çıkış", "Masraf", "KASA"].map((h) => <th key={h} className={`px-2 py-2 kicker ${h === "Tarih" ? "text-left" : "text-right"}`}>{h}</th>)}
            </tr></thead>
            <tbody>
              {g.map((d) => (
                <tr key={d.tarih} onClick={() => openGun(d.tarih)} className={`border-b border-[#EFE7D6] cursor-pointer hover:bg-[#F3ECDD] ${sel === d.tarih ? "bg-[#B8933F]/5" : ""}`} data-testid={`gencer-gun-${d.tarih}`}>
                  <td className="px-2 py-1.5 tabular-nums">{fmtDate(d.tarih)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{d.af ? fmt(d.af) : ""}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{d.sf ? fmt(d.sf) : ""}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{d.pesinat ? fmt(d.pesinat) : ""}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{d.gelen_senet ? fmt(d.gelen_senet) : ""}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-[#2F6E4E]">{d.phkk ? fmt(d.phkk) : ""}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-[#2F6E4E]">{d.shkk ? fmt(d.shkk) : ""}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-[#2F6E4E]">{d.kar ? fmt(d.kar) : ""}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{d.net_kar ? fmt(d.net_kar) : ""}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-[#A63A3A]">{d.cikis ? fmt(d.cikis) : ""}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-[#B8933F]">{d.masraf ? fmt(d.masraf) : ""}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-[#B8933F]">{fmt(d.kasa)}</td>
                </tr>
              ))}
              <tr className="bg-[#B8933F] text-white font-bold sticky bottom-0">
                <td className="px-2 py-2">TOPLAM</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmt(t.af)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmt(t.sf)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmt(t.pesinat)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmt(t.gelen_senet)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmt(t.phkk)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmt(t.shkk)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmt(t.kar)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmt(t.net_kar)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmt(t.cikis)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmt(t.masraf)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmt(t.kasa)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      )}

      {view === "kasa" && period !== "month" && aylikOzet.length > 0 && (
      <div className="bg-white border border-[#E7DECC] rounded-sm overflow-hidden mb-6" data-testid="gencer-aylik-ozet">
        <div className="px-4 py-2.5 font-heading font-bold text-sm bg-[#B8933F]/5 text-[#B8933F]">Ay Bazlı Giriş / Çıkış Özeti ({aylikOzet.length} ay)</div>
        <div className="max-h-[460px] overflow-auto">
          <table className="w-full text-sm" data-testid="gencer-aylik-ozet-table">
            <thead className="sticky top-0 bg-[#F3ECDD] border-b border-[#E7DECC] z-10"><tr>
              {["Ay", "Peşinat", "Gelen Senet", "P.H.K.K", "S.H.K.K", "Toplam Giriş", "AF", "Çıkış", "Masraf", "Toplam Çıkış", "Net"].map((h) => <th key={h} className={`px-2 py-2 kicker ${h === "Ay" ? "text-left" : "text-right"}`}>{h}</th>)}
            </tr></thead>
            <tbody>
              {aylikOzet.map((m) => (
                <tr key={m.ym} className="border-b border-[#EFE7D6] hover:bg-[#F3ECDD]" data-testid={`gencer-ozet-${m.ym}`}>
                  <td className="px-2 py-1.5 font-medium">{ayLabel(m.ym)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{m.pesinat ? fmt(m.pesinat) : ""}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{m.gelen_senet ? fmt(m.gelen_senet) : ""}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{m.phkk ? fmt(m.phkk) : ""}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{m.shkk ? fmt(m.shkk) : ""}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-[#2F6E4E]">{fmt(m.giris)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{m.af ? fmt(m.af) : ""}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{m.cikis ? fmt(m.cikis) : ""}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{m.masraf ? fmt(m.masraf) : ""}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-[#A63A3A]">{fmt(m.gider)}</td>
                  <td className={`px-2 py-1.5 text-right tabular-nums font-semibold ${(m.giris - m.gider) >= 0 ? "text-[#2F6E4E]" : "text-[#A63A3A]"}`}>{fmt(m.giris - m.gider)}</td>
                </tr>
              ))}
              <tr className="bg-[#B8933F] text-white font-bold sticky bottom-0">
                <td className="px-2 py-2">TOPLAM</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmt(t.pesinat)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmt(t.gelen_senet)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmt(t.phkk)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmt(t.shkk)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmt((t.pesinat || 0) + (t.gelen_senet || 0) + (t.phkk || 0) + (t.shkk || 0))}</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmt(t.af)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmt(t.cikis)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmt(t.masraf)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmt((t.af || 0) + (t.cikis || 0) + (t.masraf || 0) + (t.gider_duzeltme || 0))}</td>
                <td className="px-2 py-2 text-right tabular-nums">{fmt(((t.pesinat || 0) + (t.gelen_senet || 0) + (t.phkk || 0) + (t.shkk || 0)) - ((t.af || 0) + (t.cikis || 0) + (t.masraf || 0) + (t.gider_duzeltme || 0)))}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      )}

      {view === "masraf" && (
        <div className="space-y-6" data-testid="gencer-masraf-panel">
          {(() => {
            const rows = g.flatMap((d) => (d.masraf_kalem || []).map((m) => ({ tarih: d.tarih, aciklama: m.aciklama || "-", tutar: m.tutar || 0 })));
            const grouped = Object.entries(rows.reduce((a, r) => { a[r.aciklama] = (a[r.aciklama] || 0) + r.tutar; return a; }, {})).sort((x, y) => y[1] - x[1]);
            const toplam = rows.reduce((s, r) => s + r.tutar, 0);
            if (rows.length === 0) return <div className="bg-white border border-[#E7DECC] rounded-sm p-10 text-center text-[#8C8377]" data-testid="gencer-masraf-empty">Bu dönemde masraf kalemi yok.</div>;
            return (
              <>
                <div className="bg-white border border-[#E7DECC] rounded-sm overflow-hidden">
                  <div className="px-4 py-2.5 font-heading font-bold text-sm bg-[#B8933F]/10 text-[#B8933F]">Masraf Kırılımı (kalem bazında) — Toplam {fmt(toplam)}</div>
                  <table className="w-full text-sm"><tbody>
                    {grouped.map(([ad, tut]) => (<tr key={ad} className="border-b border-[#EFE7D6] last:border-0"><td className="px-4 py-1.5">{ad}</td><td className="px-4 py-1.5 text-right tabular-nums text-[#B8933F]">{fmt(tut)}</td></tr>))}
                  </tbody></table>
                </div>
                <div className="bg-white border border-[#E7DECC] rounded-sm overflow-hidden">
                  <div className="px-4 py-2.5 font-heading font-bold text-sm bg-[#B8933F]/5 text-[#B8933F]">Tarih Bazlı Masraf Detayı ({rows.length})</div>
                  <div className="max-h-[460px] overflow-auto"><table className="w-full text-sm" data-testid="gencer-masraf-detay">
                    <thead className="sticky top-0 bg-[#F3ECDD] border-b border-[#E7DECC] z-10"><tr>
                      <th className="px-4 py-2 kicker text-left">Tarih</th><th className="px-4 py-2 kicker text-left">Açıklama</th><th className="px-4 py-2 kicker text-right">Tutar</th></tr></thead>
                    <tbody>
                      {[...rows].sort((a, b) => a.tarih.localeCompare(b.tarih)).map((r, i) => (<tr key={i} className="border-b border-[#EFE7D6]"><td className="px-4 py-1.5 tabular-nums">{fmtDate(r.tarih)}</td><td className="px-4 py-1.5">{r.aciklama}</td><td className="px-4 py-1.5 text-right tabular-nums text-[#B8933F]">{fmt(r.tutar)}</td></tr>))}
                      <tr className="bg-[#B8933F] text-white font-bold sticky bottom-0"><td className="px-4 py-2" colSpan={2}>TOPLAM</td><td className="px-4 py-2 text-right tabular-nums">{fmt(toplam)}</td></tr>
                    </tbody>
                  </table></div>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {detay && (
        <div className="space-y-6" data-testid="gencer-gunluk">
          <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-[#A63A3A]/5 border border-[#A63A3A]/20 rounded-sm" data-testid="gencer-geri-al-bar">
            <div className="text-sm text-[#8C8377]"><b className="text-[#26231E]">{fmtDate(detay.tarih)}</b> — yanlış yükleme olduysa bu günü geri alabilirsiniz.</div>
            <div className="flex gap-2">
              <button onClick={() => geriAl(detay.tarih, "kasa")} data-testid="gencer-geri-al-kasa" className="px-3 py-1.5 border border-[#A63A3A]/40 text-[#A63A3A] rounded-sm text-sm font-medium hover:bg-[#A63A3A]/10">Sadece Ana Kasa</button>
              <button onClick={() => geriAl(detay.tarih, "tam")} data-testid="gencer-geri-al-tam" className="px-3 py-1.5 bg-[#A63A3A] text-white rounded-sm text-sm font-medium hover:bg-[#8a2f2f]">Tam Geri Al (Cari dahil)</button>
            </div>
          </div>
          <div className="bg-white border border-[#E7DECC] rounded-sm overflow-hidden">
            <div className="px-4 py-2.5 font-heading font-bold text-sm bg-[#B8933F]/5 text-[#B8933F]">{fmtDate(detay.tarih)} — Günlük Satışlar ({detay.satislar.length})</div>
            <div className="max-h-[360px] overflow-auto">
              <table className="w-full text-sm"><thead className="sticky top-0 bg-[#F3ECDD] border-b border-[#E7DECC] z-10"><tr>
                {["Ürün", "İsim", "AF", "SF", "Peşinat", "Gelen Senet", "P.H.K.K", "S.H.K.K", "Vade", "Teslim", "Açıklama", "Kar"].map((h) => <th key={h} className={`px-2 py-2 kicker ${["AF", "SF", "Peşinat", "Gelen Senet", "P.H.K.K", "S.H.K.K", "Kar"].includes(h) ? "text-right" : "text-left"}`}>{h}</th>)}
              </tr></thead><tbody>
                {detay.satislar.map((s, i) => (
                  <tr key={i} className="border-b border-[#EFE7D6]">
                    <td className="px-2 py-1.5">{s.urun_kodu}</td>
                    <td className="px-2 py-1.5 font-medium">{s.isim}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{s.af ? fmt(s.af) : ""}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{s.sf ? fmt(s.sf) : ""}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{s.pesinat ? fmt(s.pesinat) : ""}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{s.gelen_senet ? fmt(s.gelen_senet) : ""}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-medium text-[#B8933F]">{s.phkk ? fmt(s.phkk) : ""}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{s.shkk ? fmt(s.shkk) : ""}</td>
                    <td className="px-2 py-1.5">{s.vade}</td>
                    <td className="px-2 py-1.5 text-[#8C8377]">{s.teslim_yeri}</td>
                    <td className="px-2 py-1.5 text-[#8C8377]">{s.aciklama}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-[#2F6E4E]">{s.personel_kar ? fmt(s.personel_kar) : ""}</td>
                  </tr>
                ))}
              </tbody></table>
            </div>
          </div>

          {(() => {
            const ck = detay.ozet?.cikis_kalem || [];
            const mk = detay.ozet?.masraf_kalem || [];
            if (ck.length === 0 && mk.length === 0) return null;
            const KalemList = ({ title, items, tone }) => (
              <div className="bg-white border border-[#E7DECC] rounded-sm overflow-hidden" data-testid={`gencer-${title === "ÇIKIŞ" ? "cikis" : "masraf"}-kalem`}>
                <div className="px-4 py-2.5 font-heading font-bold text-sm flex items-center justify-between" style={{ backgroundColor: `${tone}1a`, color: tone }}>
                  <span>{title} KALEMLERİ ({items.length})</span>
                  <span className="tabular-nums">{fmt(items.reduce((s, x) => s + (x.tutar || 0), 0))}</span>
                </div>
                {items.length === 0 ? (
                  <div className="px-4 py-6 text-center text-[#8C8377] text-sm">Bu güne ait {title.toLowerCase()} kalemi yok.</div>
                ) : (
                  <table className="w-full text-sm">
                    <tbody>
                      {items.map((x, i) => (
                        <tr key={i} className="border-b border-[#EFE7D6]">
                          <td className="px-4 py-1.5">{x.aciklama || "—"}</td>
                          <td className="px-4 py-1.5 text-right tabular-nums font-medium" style={{ color: tone }}>{fmt(x.tutar)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
            return (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <KalemList title="ÇIKIŞ" items={ck} tone="#A63A3A" />
                <KalemList title="MASRAF" items={mk} tone="#B8933F" />
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
